import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';
import { onSettled } from '@theme/r-motion';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #isClosing = false;
  #openScheduled = false;
  #reopenAfterClose = false;

  /**
   * Shows the dialog.
   *
   * @param {object} [options]
   * @param {'inline-start' | 'inline-end' | 'block-end'} [options.from] Entry
   *   direction override; defaults to the `r-overlay-from` attribute / the CSS
   *   recipe tier (see the OVERLAY band in r-base.css).
   */
  showDialog({ from } = {}) {
    const { dialog } = this.refs;

    if (this.#isClosing) {
      // `dialog.open` stays true while the closing animation plays, so an early
      // return here would swallow the click — queue a reopen instead
      this.#reopenAfterClose = true;
      return;
    }

    if (dialog.open || this.#openScheduled) return;

    const direction = from ?? this.getAttribute('r-overlay-from');
    if (direction) {
      dialog.classList.remove(
        'r-overlay--from-inline-start',
        'r-overlay--from-inline-end',
        'r-overlay--from-block-end'
      );
      dialog.classList.add(`r-overlay--from-${direction}`);
    }

    this.#openScheduled = true;
    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#openScheduled = false;
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  /**
   * Closes the dialog.
   *
   * @param {object} [options]
   * @param {boolean} [options.instant] Skip the exit animation (e.g. history
   *   back-navigation closes) — equivalent to the legacy
   *   `--dialog-drawer-closing-animation: none` hook, which is still honoured.
   */
  closeDialog = async ({ instant = false } = {}) => {
    const { dialog } = this.refs;

    if (!dialog.open || this.#isClosing) return;

    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    try {
      // Cancel any in-flight entry so the exit animation starts cleanly
      for (const animation of dialog.getAnimations()) animation.cancel();

      if (instant) dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');

      dialog.classList.add('dialog-closing');

      // animationsOnly: an unrelated CSS transition inside the dialog must not
      // hold the close open (the old onAnimationEnd counted transitions too)
      await onSettled(dialog, { animationsOnly: true });

      unlockScroll(dialog);

      // A section-renderer morph can replace the dialog mid-close (e.g. the scent
      // drawer's variant swap). The scroll lock is released either way, but only
      // touch state on a still-connected dialog.
      if (!dialog.isConnected) return;

      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');
      if (instant) dialog.style.removeProperty('--dialog-drawer-closing-animation');

      this.dispatchEvent(new DialogCloseEvent());

      // Native close() restores focus to the pre-open element; if a morph
      // detached that element, fall back to the labelled trigger
      if ((document.activeElement === document.body || document.activeElement === null) && this.id) {
        const trigger = /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`));
        trigger?.focus();
      }
    } finally {
      this.#isClosing = false;
    }

    if (this.#reopenAfterClose) {
      this.#reopenAfterClose = false;
      this.showDialog();
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.#isClosing) {
      // A toggle during the closing animation means "open it again"
      this.showDialog();
    } else if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
